// ResultEntryDashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  LinearProgress,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Search,
  PlayArrow,
  CheckCircle,
  Visibility,
  Assignment,
  LocalHospital,
  Science,
  VerifiedUser,
  Refresh
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { resultEntryService } from '../../../../../../src/services/resultEntryService';

export const ResultDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pendingTests, setPendingTests] = useState([]);
  const [allTests, setAllTests] = useState([]); // Store all data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadPendingTests();
  }, [activeTab, searchTerm]); // Reload when tab or search changes

  const loadPendingTests = async () => {
    setLoading(true);
    setError('');
    try {
      // ✅ Pass status filter to backend
      const statusMap = {
        0: 'pending',
        1: 'testing',
        2: 'completed',
        3: 'verified'
      };

      const status = statusMap[activeTab];
      const response = await resultEntryService.getPendingTests(1, 50, searchTerm, status);
      console.log('API Response:', response);

      if (response.success) {
        setAllTests(response.data);
        setPendingTests(response.data); // Directly use API filtered data
      } else {
        setError(response.message || 'Failed to load pending tests');
      }
    } catch (error) {
      console.error('Error loading pending tests:', error);
      setError(error.response?.data?.message || 'Failed to load pending tests');
      showSnackbar('Error loading pending tests', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleStartResultEntry = async (resultId, serviceId) => {
    try {
      setLoading(true);
      navigate(`/hospital/laboratory/result-entry/result-dashboard/${resultId}`, {
        state: {
          serviceId: serviceId,
        }
      });
    } catch (error) {
      console.error('Error starting result entry:', error);
      showSnackbar('Failed to start result entry', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      loadPendingTests();
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // ✅ CORRECT: Get status from service_results
  const getTestStatus = (test) => {
    const serviceResults = Array.isArray(test.service_results)
      ? test.service_results
      : [test.service_results];

    if (serviceResults.every(sr => sr.status === 'released')) return 'released';
    if (serviceResults.every(sr => sr.status === 'verified')) return 'verified';
    if (serviceResults.some(sr => sr.status === 'completed')) return 'for_verification';
    if (serviceResults.some(sr => sr.status === 'testing')) return 'in_progress';
    if (serviceResults.some(sr => sr.status === 'pending')) return 'pending';
    return 'not_started';
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { color: 'warning', label: 'Ready for Testing', icon: <Science /> },
      in_progress: { color: 'info', label: 'In Progress', icon: <PlayArrow /> },
      for_verification: { color: 'primary', label: 'For Verification', icon: <VerifiedUser /> },
      verified: { color: 'success', label: 'Verified', icon: <CheckCircle /> },
      released: { color: 'secondary', label: 'Released', icon: <CheckCircle /> },
      not_started: { color: 'default', label: 'Not Started', icon: <Assignment /> }
    };
    return configs[status] || configs.not_started;
  };

  // ✅ CORRECT: Get service name from service_details
  const getServiceName = (test) => {
    if (test.service_details?.test_name) {
      return test.service_details.test_name;
    }
    if (Array.isArray(test.service_results)) {
      return test.service_results.map(sr => sr.service?.test_name || 'Unknown Test').join(', ');
    }
    return test.service_results?.service?.test_name || 'Unknown Test';
  };

  // ✅ Get service ID for navigation
  const getServiceId = (test) => {
    if (Array.isArray(test.service_results)) {
      return test.service_results[0]?.service?._id || test.service_results[0]?.service;
    }
    return test.service_results?.service?._id || test.service_results?.service;
  };

  // ✅ Get appropriate empty message for each tab
  const getEmptyMessage = () => {
    const messages = {
      0: 'No tests ready for result entry. Samples may not be collected yet.',
      1: 'No tests currently in progress.',
      2: 'No tests awaiting verification.',
      3: 'No tests have been completed yet.'
    };
    return messages[activeTab] || 'No tests found.';
  };

  const handleViewReport = (testId) => {
    navigate(`/hospital/laboratory/test-report/result-dashboard/${testId}`);
  };
  // ✅ ADD: Handle view test details function
  const handleViewTestDetails = (testId) => {
    navigate(`/hospital/laboratory/test-details/${testId}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card elevation={3}>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" gutterBottom color="primary">
              Result Entry Dashboard
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Manage laboratory test results and reporting
            </Typography>
          </Box>

          {/* Search and Filter Section */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search by Barcode, Patient Name, or UHID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="for_verification">For Verification</MenuItem>
                    <MenuItem value="verified">Verified</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={loadPendingTests}
                  startIcon={<Refresh />}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Refresh'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Tabs Section */}
          <AppBar position="static" color="default" elevation={1}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="Pending Tests" icon={<Assignment />} iconPosition="start" />
              <Tab label="In Progress" icon={<Science />} iconPosition="start" />
              <Tab label="For Verification" icon={<VerifiedUser />} iconPosition="start" />
              <Tab label="Completed" icon={<CheckCircle />} iconPosition="start" />
            </Tabs>
          </AppBar>

          {/* Tests Table */}
          {loading ? (
            <LinearProgress sx={{ mt: 2 }} />
          ) : (
            <TableContainer component={Paper} elevation={1} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Barcode</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Patient Info</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Doctor</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Test</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingTests.map((test) => {
                    const status = getTestStatus(test);
                    const statusConfig = getStatusConfig(status);
                    const serviceName = getServiceName(test);
                    const serviceId = getServiceId(test);

                    return (
                      <TableRow key={test._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {test.barcode_no}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {test.uhid}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {test.patient_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {test.age} • {test.gender}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {test.doctor?.name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={serviceName}
                            size="small"
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(test.createdAt).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(test.createdAt).toLocaleTimeString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusConfig.label}
                            color={statusConfig.color}
                            size="small"
                            icon={statusConfig.icon}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {/* Start Button - Only show for pending/in_progress tests */}
                            {(status === 'pending' || status === 'in_progress') && (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<PlayArrow />}
                                onClick={() => handleStartResultEntry(test._id, serviceId)}
                                disabled={loading}
                                sx={{ minWidth: 'auto' }}
                              >
                                Start
                              </Button>
                            )}

                            {/* Verify Button - Only show for for_verification tests */}
                            {status === 'for_verification' && (
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<VerifiedUser />}
                                onClick={() => handleStartResultEntry(test._id, serviceId)}
                                disabled={loading}
                                sx={{ minWidth: 'auto' }}
                              >
                                Verify
                              </Button>
                            )}

                            {/* View Report Button - Show for completed, verified, and released tests */}
                            {(status === 'for_verification' || status === 'verified' || status === 'released' || status === 'completed') && (
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                startIcon={<Visibility />}
                                onClick={() => handleViewReport(test._id)}
                                sx={{ minWidth: 'auto' }}
                              >
                                View Report
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && pendingTests.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <LocalHospital sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No tests found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {getEmptyMessage()}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};